import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { kelimeleriIceriyor } from '@/lib/arama';

/**
 * Gruplanabilen, aranabilir seçici.
 *
 * `ProductCombobox` ürüne özel (ad + kod arar); bu genel sürüm herhangi bir
 * listeyi alır ve başlıklı gruplar hâlinde gösterir — sipariş ekranındaki
 * "Daha Önce Alınan / Diğer Tedarikçiler" ayrımı düz açılır listede vardı,
 * aranabilir hâle getirirken kaybolmasın diye.
 *
 * options: [{ value, label, hint, group, vurgu }]
 *   hint  → sağda soluk yazı (fiyat, tarih, kod…)
 *   group → başlık; verilmezse başlıksız tek liste
 *   vurgu → önerilen seçenek (yıldızlı gösterilir)
 *
 * Seçenekler `value` bazında tekilleştirilir: `/suppliers` her para birimi
 * için ayrı satır döndüğü için aynı hesap listede 4 kez görünebiliyordu.
 */
const AZAMI_SONUC = 100;

export default function SearchableSelect({
  value,
  options = [],
  onChange,
  onOpen,
  placeholder = 'Seçin...',
  searchPlaceholder = 'Ara...',
  emptyText = 'Sonuç bulunamadı',
  disabled = false,
  className = '',
  testId,
  // Kaç seçenek çizilecek. Varsayılan 100; uzun listelerde (reçete seçimi)
  // çağıran yükseltebilir — sınır çizim maliyeti içindir, süzme her zaman
  // tüm liste üzerinde çalışır.
  azamiSonuc = AZAMI_SONUC,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const tekil = useMemo(() => {
    const gorulen = new Set();
    return options.filter(o => {
      if (!o || o.value == null || gorulen.has(o.value)) return false;
      gorulen.add(o.value);
      return true;
    });
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim();
    const uygun = q
      ? tekil.filter(o => kelimeleriIceriyor(q, `${o.label} ${o.hint || ''}`))
      : tekil;
    return uygun.slice(0, azamiSonuc);
  }, [tekil, query, azamiSonuc]);

  // Grupları ilk görülme sırasına göre koru
  const gruplar = useMemo(() => {
    const sirali = [];
    filtered.forEach(o => {
      const ad = o.group || '';
      let g = sirali.find(x => x.ad === ad);
      if (!g) { g = { ad, ogeler: [] }; sirali.push(g); }
      g.ogeler.push(o);
    });
    return sirali;
  }, [filtered]);

  const secili = tekil.find(o => o.value === value);
  const gizlenen = Math.max(0, tekil.length - filtered.length);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
        if (o && onOpen) onOpen();
      }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={`w-full justify-between border-[#d0c4b0] bg-white text-[#1a1916]
                      hover:bg-white hover:border-[#b0a090] font-normal ${className}`}
        >
          {secili
            ? <span className="truncate text-left">{secili.label}</span>
            : <span className="text-[#8a7e70] truncate">{placeholder}</span>}
          <ChevronsUpDown size={13} className="ml-2 shrink-0 text-[#8a7e70]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
        {/* shouldFilter=false: süzme yukarıda, Türkçe duyarlı yapılıyor.
            Hiçbir şey yazmadan Enter'a basınca listenin ilk öğesi seçilmesin. */}
        <Command
          shouldFilter={false}
          onKeyDown={(ev) => { if (ev.key === 'Enter' && !query.trim()) ev.preventDefault(); }}
        >
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            {gruplar.map((g) => (
              <CommandGroup key={g.ad || '_'} heading={g.ad || undefined}>
                {g.ogeler.map((o) => (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => { onChange(o.value); setQuery(''); setOpen(false); }}
                  >
                    <Check size={13} className={`mr-2 shrink-0 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate">{o.label}</span>
                    {o.hint && (
                      <span className="ml-1.5 text-xs text-[#8a7e70] shrink-0">{o.hint}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {gizlenen > 0 && (
              <div className="px-3 py-2 text-xs text-[#8a7e70] border-t border-[#e3dbcd]">
                {tekil.length} kayıttan ilk {filtered.length} tanesi gösteriliyor — aramak için yazın
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
